import { useEffect } from 'react';
import { Center, chakra, Spinner, Stack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

declare const window: Window &
    typeof globalThis & {
        hbspt: {
            forms: {
                create: (options: any) => void;
            };
        };
        ENV: {
            HUBSPOT_FORMS: Record<string, any>;
        };
    };

export const ContactForm = () => {
    const [, { language: lang }] = useTranslation();
    const portalId = window?.ENV?.HUBSPOT_PORTAL_ID;
    const formId = window?.ENV?.HUBSPOT_FORMS?.hubspot_contact_from[lang];

    useEffect(() => {
        const changeStateForTarget = (target: HTMLInputElement) => {
            const field = target?.closest('.hs-form-field:not(.hs-fieldtype-radio)');
            if (field === null || field === undefined) return;

            if (target.value.trim() !== '') {
                field.classList.add('has-value');
            } else {
                field.classList.remove('has-value');
            }
        };

        const script = document.createElement('script');
        script.src = 'https://js.hsforms.net/forms/v2.js';
        document.body.appendChild(script);

        script.addEventListener('load', () => {
            window.hbspt.forms.create({
                portalId,
                formId,
                target: '#hubspot-contact-form',
                locale: lang,
                onFormReady: ($form: HTMLFormElement) => {
                    const targets = $form.querySelectorAll<HTMLInputElement>(
                        '.hs-form-field:not(.hs-fieldtype-radio) input',
                    );
                    targets.forEach(changeStateForTarget);
                },
            });
        });

        const listenHubspotInput = (e: Event) => {
            const target = e.target as HTMLInputElement;
            changeStateForTarget(target);
        };

        document.addEventListener('input', listenHubspotInput);

        return () => {
            document.removeEventListener('input', listenHubspotInput);
        };
    }, []);

    return (
        <Stack spacing={8}>
            <HubspotContactForm id="hubspot-contact-form">
                <Center>
                    <Spinner color="general.dark" size="lg" />
                </Center>
            </HubspotContactForm>
        </Stack>
    );
};

const HubspotContactForm = styled(chakra.div)`
    .hs-form-field:not(.hs-fieldtype-radio) > label {
        position: absolute;
        top: var(--ck-fontSizes-md);
        left: var(--ck-space-3);
        font-size: var(--ck-fontSizes-md);
        font-style: normal;
        font-weight: var(--ck-fontWeights-normal);
        line-height: 135%;
        letter-spacing: -0.16px;
        color: var(--ck-colors-general-subtext);
        transition: all var(--ck-transition-duration-normal);
        user-select: none;
    }

    .hs-form-field {
        position: relative;
        margin-bottom: var(--ck-space-8);
    }

    .hs-form-field:not(.hs-fieldtype-radio):focus-within > label,
    .hs-form-field.has-value > label {
        top: -0.8rem;
        padding: 0.2rem 0.8rem;
        background: var(--ck-colors-white);
        font-size: 1.5rem;
        border-radius: 0.6rem;
    }

    .hs-fieldtype-radio > label {
        font-size: var(--ck-fontSizes-md);
        font-style: normal;
        font-weight: var(--ck-fontWeights-normal);
        line-height: 135%;
        letter-spacing: -0.16px;
        color: var(--ck-colors-general-subtext);
    }

    input[type='text'],
    input[type='email'],
    textarea {
        width: 100%;
        outline: transparent solid 2px;
        outline-offset: 2px;
        transition-property: var(--ck-transition-property-common);
        transition-duration: var(--ck-transition-duration-normal);
        font-size: var(--ck-fontSizes-md);
        padding-inline-start: var(--ck-space-3);
        padding-inline-end: var(--ck-space-3);
        height: var(--ck-sizes-13);
        border-radius: 0.3rem;
        border: var(--ck-colors-general-inputBorder) 1px solid;
        color: var(--ck-colors-general-subtext);
        background: var(--ck-colors-white);

        &.error {
            border-color: var(--ck-colors-red-500);
        }

        &:focus {
            border: var(--ck-colors-general-inputFocus) 2px solid;
            color: var(--ck-colors-general-inputFocus);
        }
    }

    textarea {
        height: auto;
        min-height: 12rem;
        padding-top: var(--ck-space-4);
        padding-bottom: var(--ck-space-4);
        padding-inline-start: var(--ck-space-3);
        padding-inline-end: var(--ck-space-3);
    }

    input[type='submit'] {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        user-select: none;
        position: relative;
        white-space: nowrap;
        vertical-align: middle;
        outline: transparent solid 2px;
        outline-offset: 2px;
        line-height: 120%;
        border-radius: 0.3rem;
        font-weight: var(--ck-fontWeights-bold);
        transition-property: var(--ck-transition-property-common);
        transition-duration: var(--ck-transition-duration-normal);
        font-family: var(--ck-fonts-primary);
        font-style: normal;
        letter-spacing: -0.16px;
        text-decoration: none;
        height: 4.8rem;
        min-width: var(--ck-sizes-13-5);
        font-size: var(--ck-fontSizes-md);
        padding-inline-start: var(--ck-space-8);
        padding-inline-end: var(--ck-space-8);
        width: 100%;
        color: var(--ck-colors-yellow-font-base);
        background: var(--ck-colors-yellow-500);
        text-transform: uppercase;
    }

    .inputs-list {
        flex-direction: row;
        gap: var(--ck-space-4);
        display: flex;
        list-style: none;

        label {
            display: inline-flex;
            align-items: center;
            vertical-align: top;
            cursor: pointer;
            position: relative;
            margin-bottom: 0;

            span {
                font-weight: var(--ck-fontWeights-normal);
                user-select: none;
                margin-inline-start: 0.5rem;
                margin-left: 1.5rem;
                font-size: var(--ck-fontSizes-md);
            }
        }
    }

    input[type='radio'] {
        display: inline-flex;
        appearance: none;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: var(--ck-sizes-4);
        height: var(--ck-sizes-4);
        border-style: solid;
        border-image: initial;
        border-radius: var(--ck-radii-full);
        border-color: var(--ck-colors-general-invisibleGray);
        color: var(--ck-colors-white);
        border-width: 1px;
        padding: var(--ck-space-2);

        &:checked {
            background: var(--ck-colors-general-inputFocus);
            border-color: var(--ck-colors-general-inputFocus);
            color: var(--ck-colors-white);

            &::before {
                content: '';
                display: inline-block;
                position: relative;
                width: 50%;
                height: 50%;
                border-radius: 50%;
                background: currentcolor;
                border-color: var(--ck-colors-chakra-border-color);
            }

            &::after {
                content: '';
                display: block;
                border-radius: 100%;
                width: var(--ck-sizes-1);
                height: var(--ck-sizes-1);
                min-width: var(--ck-sizes-1);
                min-height: var(--ck-sizes-1);
                background: var(--ck-colors-white);
                border-color: var(--ck-colors-chakra-border-color);
            }
        }
    }

    .hs-error-msg {
        display: flex;
        align-items: center;
        --form-error-color: var(--ck-colors-red-500);
        color: var(--form-error-color);
        margin-top: var(--ck-space-2);
        font-size: var(--ck-fontSizes-sm);
        line-height: var(--ck-lineHeights-normal);
        font-weight: 400;
    }
`;
